<template>
  <div class="disbursement-config-upsert">
    <KTCodePreview v-bind:title="pageTitle">
      <template v-slot:toolbar></template>

      <template v-slot:preview>
        <div v-if="onLoading" class="text-center py-5">
          <b-spinner variant="primary"></b-spinner>
        </div>

        <b-form v-else @submit.prevent="onSubmit">
          <b-row>
            <!-- Loại giải ngân -->
            <b-col cols="4">
              <b-form-group class="mb-0" label-for="parent-select">
                <template #label>
                  Loại giải ngân <span class="text-danger">*</span>
                </template>
                <b-form-select id="parent-select" v-model="form.parentId" :options="parentOptions" size="sm"
                  class="select-style" :disabled="isEditMode">
                  <template #first>
                    <b-form-select-option :value="null">Chọn loại giải ngân</b-form-select-option>
                  </template>
                </b-form-select>
              </b-form-group>
            </b-col>

            <!-- Trả góp: nhập tên; Quẹt thẻ: chọn TK từ gợi ý (name + referenceId) -->
            <b-col cols="4">
              <b-form-group class="mb-0" label-for="config-name">
                <template #label>
                  Tên nhà giải ngân <span class="text-danger">*</span>
                </template>
                <!-- Quẹt thẻ: Autosuggest tài khoản CREDIT -->
                <Autosuggest v-if="isCardParent" :model.sync="form.name" :suggestions="filteredAccountants"
                  placeholder="Chọn mã tài khoản / tên hiển thị" :limit="10" suggestion-name="text"
                  @select="onCardNameSelect" @change="onCardNameInputChange" />
                <!-- Trả góp: Autosuggest nhà trả góp -->
                <Autosuggest v-else-if="isInstallmentParent" :model.sync="form.name" :suggestions="filteredInstallments"
                  placeholder="Chọn nhà trả góp" :limit="10" suggestion-name="text" @select="onInstallmentSelect"
                  @change="onInstallmentInputChange" />
                <!-- Các loại khác: nhập tay -->
                <b-form-input v-else id="config-name" v-model="form.name" placeholder="Ví dụ: Trả góp HD" size="sm"
                  trim></b-form-input>
              </b-form-group>
            </b-col>

            <!-- Ghi chú -->
            <b-col cols="4">
              <b-form-group class="mb-0" label="Ghi chú" label-for="config-notes">
                <b-form-input id="config-notes" v-model="form.notes" placeholder="Ghi chú tuỳ chọn" size="sm"
                  trim></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
        </b-form>
      </template>

      <!-- Footer buttons theo cấu trúc dự án -->
      <template v-slot:foot>
        <b-container class="bv-example-row">
          <b-row>
            <b-button style="font-weight: 600; width: 80px" variant="primary" size="sm" :disabled="onSubmitting"
              @click="onSubmit">
              <b-spinner v-if="onSubmitting" small></b-spinner>
              <span v-else>{{ isEditMode ? 'Lưu' : 'Tạo mới' }}</span>
            </b-button>
            <b-button style="margin-left: 10px; font-weight: 600; width: 80px" variant="secondary" size="sm"
              @click="onBack">
              Quay lại
            </b-button>
          </b-row>
        </b-container>
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import ApiService from '@/core/services/api.service';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import { makeToastFaile, makeToastSuccess } from '@/utils/common';
import { TIME_OUT } from '@/utils/constants';
import { cmdUrl } from '@/utils/apiUrl';
import { PAYMENT_TYPE } from '@/utils/enum';
import Autosuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';

const CARD_KEYWORDS = [
  'quẹt thẻ',
  'quet the',
  'credit',
  'debit',
  'card',
  'thẻ',
];

const INSTALLMENT_KEYWORDS = [
  'trả góp',
  'tra gop',
  'installment',
  'góp',
  'gop',
];

const CARD_FORMULAR_TEMPLATE = {
  special: {
    fn: 'concat',
    col1: 'creditCardNo',
    col2: 'paymentAmount',
    operator: 'like',
    trimLeft: true,
    trimRight: true,
    fieldMapping: 'referenceCode',
  },
};

function stripVi(str) {
  return String(str)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd');
}

function isCardType(parentName) {
  if (!parentName) return false;
  const lower = parentName.toLowerCase();
  const ascii = stripVi(parentName);
  return CARD_KEYWORDS.some(
    (kw) => lower.includes(kw) || ascii.includes(stripVi(kw)),
  );
}

function isInstallmentType(parentName) {
  if (!parentName) return false;
  const lower = parentName.toLowerCase();
  const ascii = stripVi(parentName);
  return (
    INSTALLMENT_KEYWORDS.some(
      (kw) => lower.includes(kw) || ascii.includes(stripVi(kw)),
    ) && !isCardType(parentName)
  );
}

export default {
  name: 'UpsertDisbursementConfig',
  components: { KTCodePreview, Autosuggest },
  props: {
    id: { type: [String, Number], default: null },
  },
  data() {
    return {
      onLoading: false,
      onSubmitting: false,
      form: {
        name: '',
        notes: '',
        parentId: null,
        referenceId: '',
      },
      parentList: [],
      accountants: [],
      installments: [],
    };
  },
  computed: {
    editConfigId() {
      const raw = this.id != null && this.id !== '' ? this.id : this.$route.params.id;
      return raw != null && raw !== '' ? String(raw) : null;
    },
    isEditMode() {
      return !!this.editConfigId;
    },
    pageTitle() {
      return this.isEditMode ? 'Sửa nhà giải ngân' : 'Thêm nhà giải ngân';
    },
    parentOptions() {
      return this.parentList.map((p) => ({ value: p.id, text: p.name }));
    },
    selectedParent() {
      // b-form-select thường trả string; API id thường number → === fail → không nhận quẹt thẻ
      if (this.form.parentId == null || this.form.parentId === '') return null;
      const key = String(this.form.parentId);
      return (
        this.parentList.find((p) => String(p.id) === key) || null
      );
    },
    isCardParent() {
      return !!(this.selectedParent && isCardType(this.selectedParent.name));
    },
    isInstallmentParent() {
      return !!(this.selectedParent && isInstallmentType(this.selectedParent.name));
    },
    filteredAccountants() {
      const q = (this.form.name || '').trim().toLowerCase();
      if (!q) return this.accountants.slice(0, 10);
      return this.accountants
        .filter(
          (item) =>
            (item.value && String(item.value).toLowerCase().includes(q)) ||
            (item.text && String(item.text).toLowerCase().includes(q)),
        )
        .slice(0, 10);
    },
    filteredInstallments() {
      const q = (this.form.name || '').trim().toLowerCase();
      if (!q) return this.installments.slice(0, 10);
      return this.installments
        .filter(
          (item) => item.text && String(item.text).toLowerCase().includes(q),
        )
        .slice(0, 10);
    },
  },
  watch: {
    'form.parentId'() {
      // Tạo mới: đổi loại → bỏ reference cũ. Sửa: giữ referenceId/name từ API.
      if (!this.isEditMode) {
        this.form.referenceId = '';
        this.form.name = '';
      }
      this.syncAccountantsForParent();
      this.syncInstallmentsForParent();
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Giải ngân công nợ', route: '/disbursement' },
      { title: 'Cấu hình nhà giải ngân', route: '/disbursement/configs' },
      { title: this.isEditMode ? 'Sửa' : 'Thêm mới' },
    ]);
    this.loadParents()
      .then(() => {
        if (!this.isEditMode && this.$route.query.parentId) {
          this.form.parentId = Number(this.$route.query.parentId);
        }
        return this.isEditMode ? this.loadDetail() : Promise.resolve();
      })
      .then(() => Promise.all([
        this.syncAccountantsForParent(),
        this.syncInstallmentsForParent(),
      ]))
      .catch(() => { });
  },
  methods: {
    onBack() {
      this.$router.push({ name: 'list-disbursement-configs' });
    },
    loadParents() {
      return ApiService.query('v1/disbursement-configs', {}, { timeout: TIME_OUT })
        .then((res) => {
          const tree = res.data.data || [];
          this.parentList = tree.map((p) => ({ id: p.id, name: p.name }));
        })
        .catch(() => { });
    },
    syncAccountantsForParent() {
      if (!this.isCardParent) {
        this.accountants = [];
        return Promise.resolve();
      }
      const params = { type: PAYMENT_TYPE.CREDIT };
      return ApiService.query(cmdUrl.AccountantUrl.byType, { params })
        .then((response) => {
          const data = response.data.data || response.data || [];
          this.accountants = (Array.isArray(data) ? data : [])
            .filter((item) => item.level >= 2)
            .map((item) => ({
              id: item.id,
              code: item.code,
              value: item.code,
              text: item.name,
            }));
        })
        .catch(() => {
          this.accountants = [];
        });
    },
    syncInstallmentsForParent() {
      if (!this.isInstallmentParent) {
        this.installments = [];
        return Promise.resolve();
      }
      return ApiService.query(cmdUrl.InstallmentsUrl.getAll, {
        params: {
          limit: 1000
        }
      })
        .then((response) => {
          const data = response.data.data.data || response.data.data || [];
          this.installments = (Array.isArray(data) ? data : []).map((item) => ({
            id: item.id,
            value: item.id,
            text: item.name,
          }));
        })
        .catch(() => {
          this.installments = [];
        });
    },
    loadDetail() {
      this.onLoading = true;
      return ApiService.get(`v1/disbursement-configs/${this.editConfigId}`, {
        timeout: TIME_OUT,
      })
        .then((res) => {
          const config = res.data.data;
          this.form.name = config.name || '';
          this.form.notes = config.notes || '';
          this.form.parentId = config.parentId || null;
          this.form.referenceId = config.referenceId != null ? String(config.referenceId) : '';
        })
        .catch((error) => {
          makeToastFaile(error.response?.data?.message || 'Lỗi tải dữ liệu');
        })
        .finally(() => {
          this.onLoading = false;
        });
    },
    onCardNameSelect(option) {
      if (option && option.item) {
        this.form.referenceId = String(option.item.id || '');
        this.form.name = option.item.text;
      }
    },
    onCardNameInputChange(text) {
      if (!text || !String(text).trim()) {
        this.form.referenceId = '';
      }
    },
    onInstallmentSelect(option) {
      if (option && option.item) {
        this.form.referenceId = String(option.item.id || '');
        this.form.name = option.item.text;
      }
    },
    onInstallmentInputChange(text) {
      if (!text || !String(text).trim()) {
        this.form.referenceId = '';
      }
    },
    buildPayload() {
      const formular =
        this.selectedParent && isCardType(this.selectedParent.name)
          ? CARD_FORMULAR_TEMPLATE
          : null;
      const referenceId = this.form.referenceId;
      return {
        name: this.form.name.trim(),
        notes: this.form.notes ? this.form.notes.trim() : null,
        parentId: this.form.parentId,
        formular,
        referenceId,
      };
    },
    validateForm() {
      if (!this.form.parentId) {
        makeToastFaile('Vui lòng chọn loại giải ngân');
        return false;
      }
      if (!this.form.name.trim()) {
        makeToastFaile('Tên nhà giải ngân không được để trống');
        return false;
      }
      if (this.isCardParent && !String(this.form.referenceId || '').trim()) {
        makeToastFaile('Quẹt thẻ: chọn một dòng trong gợi ý để gắn tài khoản (referenceId)');
        return false;
      }
      return true;
    },
    async onSubmit() {
      if (!this.validateForm()) return;
      this.onSubmitting = true;
      const payload = this.buildPayload();
      try {
        if (this.isEditMode) {
          await ApiService.put(`v1/disbursement-configs/${this.editConfigId}`, payload);
          makeToastSuccess('Cập nhật nhà giải ngân thành công');
        } else {
          await ApiService.post('v1/disbursement-configs', payload);
          makeToastSuccess('Tạo nhà giải ngân thành công');
        }
        this.$router.push({ name: 'list-disbursement-configs' });
      } catch (error) {
        makeToastFaile(error.response?.data?.message || 'Có lỗi xảy ra');
      } finally {
        this.onSubmitting = false;
      }
    },
  },
};
</script>
